import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

type CandidateHeader = {
  Nombre?: string | null;
  ApellidoP?: string | null;
  ApellidoM?: string | null;
  Lema?: string | null;
  Fotografia?: string | null;
  LogoPartido?: string | null;
  Partido?: string | null;
};

type SurveyQuestion = {
  id: number;
  Tipo: string;
  Descripcion?: string | null;
  ValoresResp?: string | null;
};

type SurveyPayload = {
  R_Candidato_id?: number;
  Titulo?: string | null;
  R_Candidato?: CandidateHeader | null;
  Preguntasx?: SurveyQuestion[];
};

export default function Encuestas() {
  const location = useLocation();
  const toast = useToast();
  const surveyAccess = React.useMemo(() => {
    const parts = location.pathname.split('/');
    return parts[parts.length - 1];
  }, [location.pathname]);

  const [data, setData] = React.useState<SurveyPayload | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const { handleSubmit, register, formState: { isSubmitting } } = useForm();

  React.useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const publicResponse = await fetch(`${process.env.REACT_APP_URL_SERVER}/encuesta_publica/${surveyAccess}`);
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          setData(result);
          return;
        }

        const formData = new FormData();
        formData.append('id', surveyAccess);
        const legacyResponse = await fetch(process.env.REACT_APP_URL_SERVER + '/encuesta', {
          method: 'POST',
          body: formData,
        });
        const legacyResult = await legacyResponse.json();
        setData(legacyResult);
      } catch (error) {
        console.error('Error fetching encuesta:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSurvey();
  }, [surveyAccess]);

  const onSubmit = async (values: Record<string, string>) => {
    const respuestas = Object.entries(values).map(([key, value]) => ({
      id: key.replace('OBS', ''),
      valor: value,
    }));

    const formData = new FormData();
    formData.append('id', surveyAccess);
    formData.append('respuestas', JSON.stringify(respuestas));

    const response = await fetch(process.env.REACT_APP_URL_SERVER + '/respuesta', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      toast({ title: 'No fue posible guardar la encuesta', status: 'error', duration: 3000 });
      return;
    }

    toast({ title: 'Encuesta guardada', status: 'success', duration: 2500 });
    window.location.reload();
  };

  const candidateName = [
    data?.R_Candidato?.Nombre,
    data?.R_Candidato?.ApellidoP,
    data?.R_Candidato?.ApellidoM,
  ].filter(Boolean).join(' ');
  const candidatePhoto = data?.R_Candidato?.Fotografia || undefined;
  const candidateLogo = data?.R_Candidato?.LogoPartido || undefined;

  if (isLoading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.50">
        <Text color="gray.500">Cargando encuesta...</Text>
      </Flex>
    );
  }

  if (!data) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.50">
        <Text color="gray.500">No se encontro la encuesta.</Text>
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg="linear-gradient(180deg, #eef2ff 0%, #ffffff 32%)" py={{ base: 8, md: 12 }}>
      <Container maxW="5xl">
        <Stack spacing={8}>
          <Box
            borderRadius="28px"
            overflow="hidden"
            bg="white"
            boxShadow="0 20px 70px rgba(31, 41, 55, 0.10)"
            border="1px solid"
            borderColor="gray.100"
          >
            <Box bg="linear-gradient(135deg, #312e81 0%, #4338ca 58%, #6366f1 100%)" px={{ base: 6, md: 8 }} py={{ base: 7, md: 9 }}>
              <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={6} direction={{ base: 'column', md: 'row' }}>
                <Stack spacing={4} color="white" flex="1">
                  <Text fontSize="sm" textTransform="uppercase" letterSpacing="0.12em" color="whiteAlpha.800">
                    Encuesta de campana
                  </Text>
                  <Heading fontSize={{ base: '3xl', md: '4xl' }} lineHeight="1.05">
                    {data.Titulo || 'Encuesta sin titulo'}
                  </Heading>
                  <Stack spacing={1}>
                    <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="700">
                      {candidateName || 'Candidato'}
                    </Text>
                    {data.R_Candidato?.Lema ? (
                      <Text fontSize="md" color="whiteAlpha.900">
                        {data.R_Candidato.Lema}
                      </Text>
                    ) : null}
                    {data.R_Candidato?.Partido ? (
                      <Text fontSize="sm" color="whiteAlpha.800">
                        {data.R_Candidato.Partido}
                      </Text>
                    ) : null}
                  </Stack>
                </Stack>

                <Flex
                  minW={{ base: '100%', md: '320px' }}
                  justify={{ base: 'space-between', md: 'flex-end' }}
                  align="center"
                  gap={4}
                  wrap="wrap"
                >
                  <Avatar
                    size="2xl"
                    name={candidateName}
                    src={candidatePhoto}
                    bg="whiteAlpha.300"
                    color="white"
                    border="3px solid"
                    borderColor="whiteAlpha.500"
                  />
                  {candidateLogo ? (
                    <Box
                      w="92px"
                      h="92px"
                      borderRadius="24px"
                      bg="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      p={3}
                      boxShadow="lg"
                    >
                      <Box
                        as="img"
                        src={candidateLogo}
                        alt="Logo del candidato"
                        maxW="100%"
                        maxH="100%"
                        objectFit="contain"
                      />
                    </Box>
                  ) : null}
                </Flex>
              </Flex>
            </Box>
          </Box>

          <Box
            as="form"
            onSubmit={handleSubmit(onSubmit)}
            bg="white"
            borderRadius="28px"
            p={{ base: 6, md: 8 }}
            boxShadow="0 20px 70px rgba(31, 41, 55, 0.08)"
            border="1px solid"
            borderColor="gray.100"
          >
            <Stack spacing={6}>
              {(data.Preguntasx || []).map((question, index) => (
                <Box key={question.id} border="1px solid" borderColor="gray.100" borderRadius="22px" p={{ base: 4, md: 5 }}>
                  <Stack spacing={4}>
                    <Text color="gray.800" fontWeight="800" fontSize="lg">
                      {index + 1}. {question.Descripcion}
                    </Text>

                    {question.Tipo === '1' ? (
                      <FormControl>
                        <FormLabel fontSize="sm" color="gray.500">Tu respuesta</FormLabel>
                        <Input
                          h="52px"
                          borderRadius="18px"
                          placeholder="Escribe tu respuesta"
                          {...register(`${question.id}OBS`, { required: true })}
                        />
                      </FormControl>
                    ) : (
                      <FormControl>
                        <FormLabel fontSize="sm" color="gray.500">Selecciona una opcion</FormLabel>
                        <RadioGroup>
                          <Stack spacing={3}>
                            {(question.ValoresResp || '').split(',').map((option) => {
                              const trimmed = option.trim();
                              if (!trimmed) return null;
                              return (
                                <Box key={`${question.id}-${trimmed}`} border="1px solid" borderColor="gray.100" borderRadius="18px" px={4} py={3}>
                                  <Radio value={trimmed} {...register(`${question.id}OBS`, { required: true })}>
                                    {trimmed}
                                  </Radio>
                                </Box>
                              );
                            })}
                          </Stack>
                        </RadioGroup>
                      </FormControl>
                    )}
                  </Stack>
                </Box>
              ))}

              <Flex justify="center" pt={2}>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  bg="#312e81"
                  color="white"
                  _hover={{ bg: '#3730a3' }}
                  borderRadius="18px"
                  px={10}
                  h="52px"
                >
                  Guardar
                </Button>
              </Flex>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
